import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BackButton from "../../components/BackButton";
import TabletLayoutPage, { TabletContainer } from "../../components/Layout/TabletLayout";
import { MainColumn, RightRail, TwoColumnGrid } from "../../components/Layout/TwoColumn";
import { displayLeagueName, getMainLeague, toMs } from "../../../config/utils";
import { showTranslated, translate } from "../../../config/translator";
import client from "../../../client";
import { AdPositionEnum, SquadList as SquadListType, Team } from "../../../client/api";
import Table from "../../components/Table";
import TeamLastMatch from "../../components/Team/TeamLastMatch";
import TeamLastMatches from "../../components/Team/TeamLastMatches";
import SquadList from "../../components/Team/SquadList";
import EmblemImage from "../../components/EmblemImage";
import LeagueMatchSection, { type Section } from "../../components/LeagueMatches";
import RoundedRectangle from "../../components/RoundedRectangle";
import { allowedViews, pathLeagueView, pathTeamManagement, pathTeamView, type View } from "../../../config/routes";
import { ClavaContext } from "../../../config/contexts";
import StoreBanner from "../../components/StoreBanner";
import SegmentedTabs from "../../components/SegmentedTabs";
import { AdSlot } from "../../components/AdSlot";
import { useSquadListController } from "../../components/Team/useSquadListController";
import { fetchTeamMatches } from "../../../store/actions/matchActions";
import { addFavorite, removeFavorite } from "../../../store/actions/userActions";
import { AppDispatch, RootState } from "../../../store";
import { AdSensePositionEnum } from "../../../config/adsenseConfig";

const isAllowedView = (v?: string): v is View =>
  !!v && allowedViews.includes(v as View);

const TeamPage: React.FC = () => {
  const {
    teamId: teamIdParam,
    view: viewParam
  } = useParams<{
    teamId?: string;
    view?: string;
  }>();

  const teamId = useMemo(() => {
    const n = teamIdParam ? parseInt(teamIdParam, 10) : NaN;
    return Number.isFinite(n) ? n : undefined;
  }, [teamIdParam]);

  const view: View | undefined = useMemo(
    () => (isAllowedView(viewParam) ? (viewParam as View) : undefined),
    [viewParam]
  );

  const favorites = useSelector((state: RootState) => state.user.favorites);
  const { user, l } = useContext(ClavaContext);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<Team | null>(null);
  const [squad, setSquad] = useState<SquadListType | undefined>(undefined);
  const [squadLoading, setSquadLoading] = useState(false);
  const headerRef = useRef<HTMLDivElement | null>(null);

  const allTeamMatches = useSelector<RootState, RootState["match"]["teamMatches"]>(s => s.match.teamMatches);
  const teamMatches = useMemo(() => allTeamMatches[`${teamId}`] ?? [], [teamId, allTeamMatches]);
  useEffect(() => {
    if (!teamId) return;
    client().setLang(l);
    import("../../../client/api").then(({ TeamService }) => {
      TeamService.getTeamByIdTeamTeamIdGet(teamId).then(setTeam);
    });
    setSquadLoading(true);
    client()
      .getSquad(teamId)
      .then(
        (res) => {
          setSquad(res);
          setSquadLoading(false);
        },
        () => setSquadLoading(false)
      );
  }, [teamId, l]);

  useEffect(() => {
    if (!teamId) return;

    if (teamMatches.length === 0) {
      fetchTeamMatches(dispatch, teamId);
    }
  }, [teamId, dispatch, teamMatches.length]);

  const mainLeague = useMemo(
    () => (team?.leagues ? getMainLeague(team.leagues) : undefined),
    [team]
  );
  const leagueId = useMemo(() => mainLeague?.id, [mainLeague]);


  const lastPlayedMatch = useMemo(() => {
    const now = Date.now();
    return (
      teamMatches
        .filter((m) => {
          const endMs = toMs(m.endTime);
          return Number.isFinite(endMs) && endMs <= now;
        })
        .sort((a, b) => toMs(b.startTime) - toMs(a.startTime))[0] ?? null
    );
  }, [teamMatches]);

  const playedMatchesAsc = useMemo(() => {
    const now = Date.now();
    return teamMatches
      .filter((m) => {
        const endMs = toMs(m.endTime);
        return Number.isFinite(endMs) && endMs <= now;
      })
      .sort((a, b) => toMs(a.startTime) - toMs(b.startTime));
  }, [teamMatches]);

  const upcomingMatchesAsc = useMemo(() => {
    const now = Date.now();
    return teamMatches
      .filter((m) => {
        const startMs = toMs(m.startTime);
        return Number.isFinite(startMs) && startMs > now;
      })
      .sort((a, b) => toMs(a.startTime) - toMs(b.startTime));
  }, [teamMatches]);

  const playedMatchesSection = useMemo<Section>(() => {
    const titleName = team?.leagues?.[0]
      ? showTranslated(team.leagues[0].name, l)
      : translate("matches", l);
    const titleId =
      typeof team?.leagues?.[0]?.id === "number"
        ? team.leagues[0].id
        : undefined;

    return {
      title: { name: titleName, id: titleId },
      data: playedMatchesAsc.map((match, idx) => ({
        ...match,
        idx,
        idx2: idx
      }))
    } as Section;
  }, [playedMatchesAsc, team, l]);

  const upcomingMatchesSection = useMemo<Section>(
    () => ({
      title: { name: translate("nextmatches", l), id: undefined },
      data: upcomingMatchesAsc.map((match, idx) => ({
        ...match,
        idx,
        idx2: idx
      }))
    }),
    [upcomingMatchesAsc, l]
  );
  const {
    sortDir,
    sortField,
    q: squadQ,
    setQ: setSquadQ,
    filteredSquad,
    onSortGiven,
    onSortFamily,
    onSortPosition
  } = useSquadListController(squad, mainLeague, l);

  const isFollowing = useMemo(
    () => favorites.some((fav) => fav.type === "team" && fav.id === teamId),
    [favorites, teamId]
  );

  const handleFollowToggle = () => {
    if (!user || !teamId) return;

    if (isFollowing) {
      removeFavorite(dispatch, { id: teamId, type: "team" });
    } else {
      addFavorite(dispatch, { id: teamId, type: "team" });
    }
  };

  return (
    <TabletLayoutPage>
      <TabletContainer className="TeamPage">
        <TwoColumnGrid>
          <MainColumn>
            <div className="flex flex-col relative">
              <RoundedRectangle
                ref={headerRef}
                className="rounded-xl mb-4 bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pt-20"
                hasBorder
                hasTitle={false}
              >
                <BackButton />
                {team ? (
                  <div className="flex items-center gap-4 px-6 xl:px-8 pb-8">
                    <EmblemImage
                      image={team.emblem}
                      width={64}
                      className="shrink-0"
                    />
                    <div className="flex flex-col">
                      <span className="text-base font-medium text-font dark:text-font-dark">
                        {showTranslated(team.name, l)}
                      </span>
                      {mainLeague ? (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              pathLeagueView(mainLeague.id, "table")
                            )
                          }
                          className="text-left"
                        >
                          <span
                            className="text-sm font-normal text-font-600 dark:text-font-600-dark hover:text-primary dark:hover:text-primary transition-colors cursor-pointer">
                            {displayLeagueName(mainLeague, l)}
                          </span>
                        </button>
                      ) : null}
                    </div>
                    <div className="flex-1" />
                    {user?.groups?.some((g) => g.team.id === team.id) ? (
                      <NavLink
                        to={pathTeamManagement()}
                        className="rounded-full py-2 px-4 flex flex-row items-center justify-center border-0 bg-insider dark:bg-insider-dark hover:opacity-90 active:opacity-95 text-font-dark"
                      >
                        {translate("teamManagement", l)}
                      </NavLink>
                    ) : user && teamId ? (
                      <button
                        type="button"
                        onClick={handleFollowToggle}
                        className={`rounded-full h-9 py-1 px-3 flex justify-center items-center gap-1 transition-colors ${
                          isFollowing
                            ? "bg-primary-50 dark:bg-primary-50-dark text-font-300 dark:text-font-300-dark"
                            : "bg-bg-50 dark:bg-bg-50-dark text-primary dark:text-primary"
                        }`}
                      >
                        {translate(isFollowing ? "following" : "follow", l)}
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <span className="text-font-600 dark:text-font-600-dark px-6 xl:px-8">
                    {translate("loading", l)}
                  </span>
                )}
              </RoundedRectangle>

              {/* Mobile: last match + list */}
              <div className="block xl:hidden">
                <TeamLastMatch
                  match={lastPlayedMatch}
                  isMainLeagueOverride={(() => {
                    const last = lastPlayedMatch;
                    if (!last) return undefined;
                    const computedMainLeagueId = team?.leagues
                      ? getMainLeague(team.leagues)?.id
                      : undefined;
                    if (typeof computedMainLeagueId !== "number")
                      return undefined;
                    return last.leagueCupId === computedMainLeagueId;
                  })()}
                />
              </div>
              <div className="block xl:hidden mt-5">
                <TeamLastMatches
                  teamId={team?.id ?? undefined}
                  matches={teamMatches}
                />
              </div>

              {/* Tabs */}
              <SegmentedTabs
                className="justify-start w-full pt-3 pb-6 xl:pt-2 xl:pb-4"
                activeKey={view ?? "table"}
                replace
                items={[
                  {
                    key: "table",
                    label: translate("table", l),
                    to:
                      typeof teamId === "number"
                        ? pathTeamView(teamId, "table")
                        : "#"
                  },
                  {
                    key: "matches",
                    label: translate("matches", l),
                    to:
                      typeof teamId === "number"
                        ? pathTeamView(teamId, "matches")
                        : "#"
                  },
                  {
                    key: "squad",
                    label: translate("TeamSquad", l),
                    to:
                      typeof teamId === "number"
                        ? pathTeamView(teamId, "squad")
                        : "#"
                  },
                  {
                    key: "statistics",
                    label: translate("statistics", l),
                    to:
                      typeof teamId === "number"
                        ? pathTeamView(teamId, "statistics")
                        : "#"
                  }
                ]}
              />

              {/* TAB: table */}
              <div className={!view || view === "table" ? "" : "hidden"}>
                {leagueId ? (
                  <div className="rounded-xl mb-4  w-full">
                    <Table team1Id={0} team2Id={0} leagueId={leagueId} />
                  </div>
                ) : (
                  <div className="p-4 text-font-600 dark:text-font-600-dark">
                    {translate("loading", l)}
                  </div>
                )}
              </div>

              {/* TAB: statistics */}
              <div className={view === "statistics" ? "" : "hidden"}>
                <RoundedRectangle
                  className="rounded-xl bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4  w-full"
                  hasBorder
                  hasTitle
                  titleType="upCenter"
                  titleKey="statistics"
                >
                  <StoreBanner
                    border={false}
                    message={translate("downloadUnlimited", l)}
                  />
                </RoundedRectangle>
              </div>

              {/* TAB: matches (played) */}
              <div className={view === "matches" ? "" : "hidden"}>
                <RoundedRectangle
                  className="w-full bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4"
                  hasBorder
                  hasTitle
                  titleType="upLeft"
                  titleKey="alreadyPlayed"
                  titleClickable={typeof team?.leagues?.[0]?.id === "number"}
                  onTitleClick={() => {
                    const id = team?.leagues?.[0]?.id;
                    if (typeof id === "number") {
                      navigate(pathLeagueView(id, "matches"));
                    }
                  }}
                >
                  <div className="tablet-panel-body mt-3">
                    {playedMatchesSection.data.length > 0 ? (
                      <LeagueMatchSection
                        section={playedMatchesSection}
                        matchBanner="date-league"
                        isMainLeagueOverride={(match) =>
                          leagueId
                            ? match.leagueCupId === leagueId
                            : undefined
                        }
                      />
                    ) : (
                      <div className="text-font-600 dark:text-font-600-dark">
                        {translate("noMatches", l)}
                      </div>
                    )}
                  </div>
                </RoundedRectangle>
              </div>

              {/* TAB: squad */}
              <div className={view === "squad" ? "" : "hidden"}>
                <RoundedRectangle
                  className="w-full bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4"
                  hasBorder
                  hasTitle
                  titleType="upLeft"
                  titleKey="TeamSquad"
                  titleSuffix={
                    squad ? (
                      <span className="text-font-600 dark:text-font-600-dark font-semibold">
                        {" "}
                        {`(${squad.length})`}
                      </span>
                    ) : null
                  }
                >
                  <div className="tablet-panel-body mt-3">
                    <SquadList
                      squad={squad}
                      filteredSquad={filteredSquad}
                      leagueForPosition={mainLeague}
                      loading={squadLoading}
                      q={squadQ}
                      onChangeQ={setSquadQ}
                      sortField={sortField}
                      sortDir={sortDir}
                      onSortGiven={onSortGiven}
                      onSortFamily={onSortFamily}
                      onSortPosition={onSortPosition}
                      showNewButton={false}
                      showBirthColumn={false}
                      showTitle={false}
                    />
                  </div>
                </RoundedRectangle>
              </div>

              <div className={view === "matches" ? "" : "hidden"}>
                <RoundedRectangle
                  className="w-full bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark shadow-card dark:shadow-card-dark mb-4"
                  hasBorder
                  hasTitle
                  titleType="upLeft"
                  titleKey="nextmatches"
                >
                  <div className="tablet-panel-body mt-3">
                    {upcomingMatchesSection.data.length > 0 ? (
                      <LeagueMatchSection
                        section={upcomingMatchesSection}
                        matchBanner="date-league"
                        isMainLeagueOverride={(match) =>
                          leagueId
                            ? match.leagueCupId === leagueId
                            : undefined
                        }
                      />
                    ) : (
                      <div className="text-font-600 dark:text-font-600-dark">
                        {translate("noMatches", l)}
                      </div>
                    )}
                  </div>
                </RoundedRectangle>
              </div>
              <div className="mb-4">
                <AdSlot
                  type={AdPositionEnum.TEAM_SCREEN_BANNER}
                  className="w-full"
                />
              </div>
            </div>
          </MainColumn>

          <RightRail className="gap-4">
            <TeamLastMatch
              match={lastPlayedMatch}
              isMainLeagueOverride={(() => {
                const last = lastPlayedMatch;
                if (!last) return undefined;
                const computedMainLeagueId = team?.leagues
                  ? getMainLeague(team.leagues)?.id
                  : undefined;
                if (typeof computedMainLeagueId !== "number") return undefined;
                return last.leagueCupId === computedMainLeagueId;
              })()}
            />
            <AdSlot
              type={AdSensePositionEnum.TEAM_RIGHT_RAIL}
              className="w-full"
              priority="adsense"
            />
            <TeamLastMatches
              teamId={team?.id ?? undefined}
              matches={teamMatches}
            />
          </RightRail>
        </TwoColumnGrid>
      </TabletContainer>
    </TabletLayoutPage>
  );
};

export default TeamPage;
